import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  MessageSquare,
  Rocket,
  Lock,
  AlertTriangle,
  Loader2,
  RotateCcw,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/toast';
import type { IpSeed } from '@starfox/shared-types';
import { EpisodeCountPicker } from './EpisodeCountPicker';
import { StorylineEditor } from './StorylineEditor';
import { StorylineQuickEditor } from './StorylineQuickEditor';

interface EpisodeItem {
  index: number;
  title?: string;
  narrative?: string;
  updatedAt?: string;
}

interface Props {
  ipSeed: IpSeed | null;
  onSwitch: (path: 'path3') => void;
  loading?: boolean;
  status: 'draft' | 'initialized';
  audienceLocked: boolean;
  onInitialize: () => void;
  onReinitialize: () => void;
  initializing: boolean;
  onJumpToAudience: () => void;
  episodeId: string;
  episodeCount: number;
  episodeDuration: number;
  scriptMode?: string | null;
  hasSelectedEpisodeCount: boolean;
  worldText: string;
  charactersText: string;
  episodes: EpisodeItem[];
  onStorylineSaved: () => void;
  onEpisodeCountSaved: (n: number) => void;
  onEpisodeDurationChange: (d: number) => void;
  agentCompletedAt: string | null;
  writerCompletedAt: string | null;
  onOpenAgent: () => void;
}

export function HotspotWorkspace({
  ipSeed,
  onSwitch,
  loading,
  status,
  audienceLocked,
  onInitialize,
  onReinitialize,
  initializing,
  onJumpToAudience,
  episodeId,
  episodeCount,
  episodeDuration,
  scriptMode,
  hasSelectedEpisodeCount,
  worldText,
  charactersText,
  episodes,
  onStorylineSaved,
  onEpisodeCountSaved,
  onEpisodeDurationChange,
  agentCompletedAt,
  writerCompletedAt,
  onOpenAgent,
}: Props) {
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-24 animate-pulse rounded-lg bg-surface-variant" />
        <div className="h-12 animate-pulse rounded-lg bg-surface-variant" />
      </div>
    );
  }

  const isDraft = status === 'draft';
  const editable = !isDraft;
  const [fullscreenFull, setFullscreenFull] = useState(false);

  const plotsCompleted = episodes.filter((e) => !!e.narrative).length;
  const plotsReady = plotsCompleted > 0;

  if (!ipSeed || !ipSeed.ipName) {
    return (
      <div className="space-y-3 text-sm">
        <p className="text-neutral-600">
          当前剧集没有 IP 元数据(可能是直接立项的非 IP 化 Topic)。建议改用其他路径开始创作。
        </p>
        {isDraft ? (
          <div className="space-y-2">
            {!audienceLocked && <AudiencePrompt onJump={onJumpToAudience} />}
            {audienceLocked && !plotsReady && (
              <div className="rounded-lg border border-warning/30 bg-warning-soft/30 px-3 py-2 text-xs text-warning">
                剧情脚本尚未全部生成（{plotsCompleted}/{episodeCount} 集），生成完毕后方可初始化
              </div>
            )}
            <Button
              variant="agent"
              agentColor="writer"
              onClick={onInitialize}
              disabled={!audienceLocked || !plotsReady || initializing}
            >
              <Rocket className="h-4 w-4" />
              {initializing ? '初始化中…' : '项目初始化'}
            </Button>
            <p className="text-xs text-neutral-500">
              初始化后即可进入「对话协作」/「自动生成」路径开始落 SSML 草稿。
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {agentCompletedAt ? (
              <>
                <Button variant="agent" agentColor="writer" onClick={() => onSwitch('path3')}>
                  <MessageSquare className="h-4 w-4" /> 进入脚本微调
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onReinitialize}
                  disabled={initializing || Boolean(writerCompletedAt)}
                  title={writerCompletedAt ? '已点击完成编剧，不能重新项目初始化' : '覆盖重新生成分集 S 场景与 Story Atlas'}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> 重新项目初始化
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={onOpenAgent}>
                <Loader2 className="h-4 w-4 animate-spin" /> 查看 Agent 进度
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm">
      {isDraft && (
        <div className="rounded-lg border border-brand-200 bg-brand-50/60 px-3 py-2 text-xs leading-relaxed text-brand-700">
          请在右侧 Story Atlas 中审阅自动生成的草稿(受众 / 世界 / 人物 / 大纲…)。
          确认无误后,先锁定受众画像,再点击下方「项目初始化」解锁其他创作路径。
        </div>
      )}

      <Section label="IP 名称">
        <EditableInline
          value={ipSeed.ipName ?? ''}
          field="ipName"
          episodeId={episodeId}
          editable={editable}
          renderView={(v) => (
            <p className="text-base font-semibold text-neutral-900">{v || '—'}</p>
          )}
          inputType="text"
        />
      </Section>
      <Section label="一句话钉住(Logline)">
        <EditableInline
          value={ipSeed.loglinePinned ?? ''}
          field="loglinePinned"
          episodeId={episodeId}
          editable={editable}
          renderView={(v) => <p>{v || '—'}</p>}
          inputType="textarea"
          rows={2}
        />
      </Section>
      <Section label="核心冲突">
        <EditableInline
          value={ipSeed.coreConflict ?? ''}
          field="coreConflict"
          episodeId={episodeId}
          editable={editable}
          renderView={(v) => <p>{v || '—'}</p>}
          inputType="textarea"
          rows={2}
        />
      </Section>
      <div className="grid gap-3 sm:grid-cols-2">
        <Section label="主角原型">
          <p>{ipSeed.mcArchetype || '—'}</p>
        </Section>
        <Section label="反派原型">
          <p>{ipSeed.antagonistArchetype || '—'}</p>
        </Section>
      </div>
      <Section label="开场 15 秒钩子">
        <EditableInline
          value={ipSeed.openingScene15s ?? ''}
          field="openingScene15s"
          episodeId={episodeId}
          editable={editable}
          renderView={(v) => (
            <p className="rounded-lg bg-brand-50 p-3 text-brand-700">{v || '—'}</p>
          )}
          inputType="textarea"
          rows={3}
        />
      </Section>
      <Section label="爽点节奏(Cheer Points)">
        <EditableCheerPoints
          value={ipSeed.cheerPoints ?? []}
          episodeId={episodeId}
          editable={editable}
        />
      </Section>

      {ipSeed.characters && ipSeed.characters.length > 0 && (
        <Section label="出场人物">
          <div className="grid gap-2 sm:grid-cols-2">
            {ipSeed.characters.map((character) => (
              <div key={character.name} className="rounded-md border border-outline-variant/30 bg-surface p-2 text-xs">
                <div className="font-semibold text-neutral-800">{character.name || '—'}</div>
                <div className="mt-1 text-neutral-500">{character.role || character.identity || '—'}</div>
                {character.speakingStyle && (
                  <div className="mt-1 text-neutral-400">台词风格：{character.speakingStyle}</div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <EpisodeCountPicker
        episodeId={episodeId}
        value={episodeCount}
        hasSelected={hasSelectedEpisodeCount}
        onSaved={(n, d) => { onEpisodeCountSaved(n); onEpisodeDurationChange(d); }}
        locked={!isDraft}
        episodeDuration={episodeDuration}
        onDurationChange={onEpisodeDurationChange}
      />

      <StorylineQuickEditor
          episodeId={episodeId}
          initial={ipSeed.storyline ?? ''}
          onSaved={onStorylineSaved}
          locked={!isDraft}
        />

      <div className="relative">
        <StorylineEditor
          episodeId={episodeId}
          audienceLocked={audienceLocked}
          hasSelectedEpisodeCount={hasSelectedEpisodeCount}
          episodeCount={episodeCount}
          episodeDuration={episodeDuration}
          generationMode={scriptMode === 'premium' ? 'premium' : 'normal_hongguo'}
          worldText={worldText}
          charactersText={charactersText}
          episodes={episodes}
          onSaved={onStorylineSaved}
          onJumpToAudience={onJumpToAudience}
          onFullscreen={isDraft ? () => setFullscreenFull(true) : undefined}
          locked={!isDraft}
        />
      </div>

      <FullscreenModal
        open={fullscreenFull}
        title="剧情脚本 · 全屏编辑"
        onClose={() => setFullscreenFull(false)}
      >
        <StorylineEditor
          episodeId={episodeId}
          audienceLocked={audienceLocked}
          hasSelectedEpisodeCount={hasSelectedEpisodeCount}
          episodeCount={episodeCount}
          episodeDuration={episodeDuration}
          generationMode={scriptMode === 'premium' ? 'premium' : 'normal_hongguo'}
          worldText={worldText}
          charactersText={charactersText}
          episodes={episodes}
          onSaved={() => { onStorylineSaved(); setFullscreenFull(false); }}
          onJumpToAudience={onJumpToAudience}
          locked={!isDraft}
        />
      </FullscreenModal>

      {isDraft ? (
        <div className="space-y-2 pt-2">
          {!audienceLocked && <AudiencePrompt onJump={onJumpToAudience} />}
          {audienceLocked && !plotsReady && (
            <div className="rounded-lg border border-warning/30 bg-warning-soft/30 px-3 py-2 text-xs text-warning">
              剧情脚本尚未全部生成（{plotsCompleted}/{episodeCount} 集），生成完毕后方可初始化
            </div>
          )}
          <Button
            variant="agent"
            agentColor="writer"
            onClick={onInitialize}
            disabled={!audienceLocked || !plotsReady || initializing}
          >
            <Rocket className="h-4 w-4" />
            {initializing ? '初始化中…' : '项目初始化'}
          </Button>
          <p className="text-xs text-neutral-500">
            初始化后即可进入「对话协作」/「自动生成」路径开始落 SSML 草稿。
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 pt-2">
          {agentCompletedAt ? (
            <>
              <Button variant="agent" agentColor="writer" onClick={() => onSwitch('path3')}>
                <MessageSquare className="h-4 w-4" /> 采用此设定 · 进入脚本微调
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onReinitialize}
                disabled={initializing || Boolean(writerCompletedAt)}
                title={writerCompletedAt ? '已点击完成编剧，不能重新项目初始化' : '覆盖重新生成分集 S 场景与 Story Atlas'}
              >
                <RotateCcw className="h-3.5 w-3.5" /> 重新项目初始化
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onOpenAgent}>
              <Loader2 className="h-4 w-4 animate-spin" /> 查看 Agent 进度
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function AudiencePrompt({ onJump }: { onJump: () => void }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning-soft/30 px-3 py-2 text-xs text-warning">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1 leading-relaxed">
        受众画像尚未锁定,先确认目标人群才能完成项目初始化。
      </span>
      <button
        type="button"
        onClick={onJump}
        className="inline-flex items-center gap-1 rounded-full bg-warning px-2.5 py-1 text-[11px] text-white shadow-sm transition hover:opacity-90"
      >
        <Lock className="h-3 w-3" /> 立即锁定画像
      </button>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wider text-outline">{label}</div>
      <div className="text-sm text-neutral-800">{children}</div>
    </div>
  );
}

type IpSeedField =
  | 'ipName'
  | 'loglinePinned'
  | 'coreConflict'
  | 'openingScene15s'
  | 'cheerPoints';

function useIpSeedMutation(episodeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Record<IpSeedField, string | string[]>>) =>
      api
        .patch(`writer/episodes/${episodeId}/ip-seed`, { json: payload })
        .json<{ ipSeed: IpSeed; updatedAt: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['writer', 'episode', episodeId] });
      qc.invalidateQueries({ queryKey: ['writer', 'projects'] });
      toast.success('已保存');
    },
    onError: (err: Error) => toast.error('保存失败', err.message || '稍后重试'),
  });
}

function EditableInline({
  value,
  field,
  episodeId,
  editable,
  renderView,
  inputType,
  rows = 2,
}: {
  value: string;
  field: Exclude<IpSeedField, 'cheerPoints'>;
  episodeId: string;
  editable: boolean;
  renderView: (v: string) => React.ReactNode;
  inputType: 'text' | 'textarea';
  rows?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  const mut = useIpSeedMutation(episodeId);

  if (!editing) {
    return (
      <div className="group flex items-start gap-2">
        <div className="flex-1">{renderView(value)}</div>
        {editable && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded p-1 text-neutral-300 opacity-0 transition hover:bg-surface-variant hover:text-brand-600 group-hover:opacity-100"
            title="编辑"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }
  const save = () => {
    const next = draft.trim();
    if (next === value.trim()) {
      setEditing(false);
      return;
    }
    mut.mutate({ [field]: next }, { onSuccess: () => setEditing(false) });
  };
  return (
    <div className="space-y-2">
      {inputType === 'textarea' ? (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={rows}
          className="w-full rounded-md border border-brand-300 bg-surface px-2.5 py-1.5 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-brand-300"
        />
      ) : (
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full rounded-md border border-brand-300 bg-surface px-2.5 py-1.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-brand-300"
        />
      )}
      <div className="flex justify-end gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft(value);
            setEditing(false);
          }}
          disabled={mut.isPending}
        >
          <X className="h-3.5 w-3.5" />
          取消
        </Button>
        <Button
          variant="agent"
          agentColor="writer"
          size="sm"
          onClick={save}
          disabled={mut.isPending}
        >
          {mut.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          保存
        </Button>
      </div>
    </div>
  );
}

function EditableCheerPoints({
  value,
  episodeId,
  editable,
}: {
  value: string[];
  episodeId: string;
  editable: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value.join('\n'));
  useEffect(() => {
    if (!editing) setDraft(value.join('\n'));
  }, [value, editing]);
  const mut = useIpSeedMutation(episodeId);

  if (!editing) {
    return (
      <div className="group flex items-start gap-2">
        <div className="flex-1 flex flex-wrap gap-1.5">
          {value.length > 0 ? (
            value.map((c, i) => (
              <span
                key={i}
                className="rounded-full bg-warning-soft/60 px-2.5 py-1 text-xs text-warning"
              >
                {c}
              </span>
            ))
          ) : (
            <span className="text-xs text-neutral-500">—</span>
          )}
        </div>
        {editable && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded p-1 text-neutral-300 opacity-0 transition hover:bg-surface-variant hover:text-brand-600 group-hover:opacity-100"
            title="编辑"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }
  const save = () => {
    const list = draft
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 12);
    mut.mutate(
      { cheerPoints: list },
      { onSuccess: () => setEditing(false) },
    );
  };
  return (
    <div className="space-y-2">
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={5}
        placeholder="每行一个爽点 · 最多 12 条"
        className="w-full rounded-md border border-brand-300 bg-surface px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-300"
      />
      <div className="flex justify-end gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft(value.join('\n'));
            setEditing(false);
          }}
          disabled={mut.isPending}
        >
          <X className="h-3.5 w-3.5" />
          取消
        </Button>
        <Button
          variant="agent"
          agentColor="writer"
          size="sm"
          onClick={save}
          disabled={mut.isPending}
        >
          {mut.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          保存
        </Button>
      </div>
    </div>
  );
}

function FullscreenModal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col bg-surface">
      <div className="flex shrink-0 items-center justify-between border-b border-outline-variant px-5 py-3">
        <span className="text-sm font-semibold text-neutral-800">{title}</span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-neutral-500 hover:bg-surface-variant hover:text-neutral-800 transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {children}
      </div>
    </div>,
    document.body,
  );
}
